import React, { useEffect, useRef, useState } from "react";
import { CustomMarker } from "react-calendar-timeline";

import { Panel, PanelGroup } from "react-resizable-panels";
import moment from "moment";
import { intl } from "LocaleWrapper";
import { useApolloClient, useLazyQuery, useMutation } from "@apollo/client";

import { Card, PageHeader } from "@cofano/cck";

import { CircularProgress, Switch } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";

import Helmet from "components/helmet/Helmet";
import useComments from "components/comments/useComments";
import CommentsWidget from "components/comments/CommentsWidget";

import { Language, usePermission } from "imports/Utils";

import { Board } from "view/controltower/ControlTowerBoard";
import ContractCreateEditDialog from "view/contractsNew/components/dialogs/ContractCreateEditDialog";

import { FormattedDateWindow } from "utils/DateUtils";

import BulkPlanningTable, { CargoFromQuery } from "./BulkPlanningTable";
import UnplannedCargoReplanDialog from "view/planning/dialogs/UnplannedCargoReplanDialog";
import CargoPlanningDialog from "../dialogs/CargoPlanningDialog";
import { gql } from "__generated__/gql";
import { Charter, Voyage } from "__generated__/graphql";
import { Cargo, CargoLinkedCharter, Ship } from "types/index";
import "./style.scss";

import { toCargoActionTimesInput } from "./BulkPlanningUtils";
import { ChildProps } from "types/commons";
import { BargeType, CharterSubtype, CharterType } from "__generated__/enums";
import ResizeHandle from "components/resizeContainer/ResizeHandle";

import { UpdateCargoActionsTimesDocument } from "__generated__/view/cargos/graphql/cargo.mutations.generated";
import { GetVoyageDocument, GetVoyageQuery } from "__generated__/view/voyages/graphql/voyage.queries.generated";
import { UnplanAllCargosAndDeleteVoyageDocument } from "__generated__/view/voyages/graphql/voyage.mutations.generated";

export type VoyageFromQuery = NonNullable<GetVoyageQuery["voyage"]>;
export type CargoActionsFromQuery = ChildProps<GetVoyageQuery, "voyage">["calls"][number]["cargos"][number];

export type BulkPlanningReplan = {
	voyage: Pick<Voyage, "id" | "voyageNumber" | "start" | "end" | "from" | "to" | "purchaseCharters">;
	newStart: number;
	newEnd: number;
	oldShip: Pick<Ship, "id" | "name">;
	newShip: Pick<Ship, "id" | "name" | "operator" | "owner" | "defaultCharterSubtype">;
	charters: Pick<Charter, "id" | "type" | "contract" | "charterNumber">[];
}

export type BulkPlanningPlanDialog = {
	open: boolean;
	ship: Pick<Ship, "id" | "name">;
	cargos: CargoFromQuery[];
}

export type BulkPlanningContractDialog = {
	open: boolean;
	charter: CargoLinkedCharter;
	createSalesContract: boolean;
}

export type BulkPlanningReplanDialog = {
	open: boolean;
	voyage: VoyageFromQuery;
	actions: CargoActionsFromQuery[];
}

const BulkPlanning = () => {

	const client = useApolloClient();

	const { isReadOnly } = usePermission();

	const [ selectedRows, setSelectedRows ] = useState<CargoFromQuery[]>([]);
	const [ dragging, setDragging ] = useState(false);
	const [ scrollToCallId, setScrollToCallId ] = useState<number | undefined>(undefined);
	const [ planningEnabled, enablePlanning ] = useState(false);
	const [ markers, setMarkers ] = useState<React.JSX.Element[]>([]);
	const [ loading, setLoading ] = useState(false);
	const [ tableKey, setTableKey ] = useState(0);
	const [ selectedVoyage, selectVoyage ] = useState<number[]>([]);
	const [ callsForComments, setCallsForComments ] = useState([]);
	const [ cargosForComments, setCargosForComments ] = useState<CargoFromQuery[]>([]);
	const [ layoutColumnSizes, setLayoutColumnSizes ] = useState(localStorage.getItem("bulkPlanningLayoutColumnSizes") ? JSON.parse(localStorage.getItem("bulkPlanningLayoutColumnSizes") || "") : [ 50, 50 ]);

	const [ replanData, setReplanData ] = useState<BulkPlanningReplan | undefined>(undefined);
	const [ planDialog, setPlanDialog ] = useState<BulkPlanningPlanDialog | undefined>(undefined);
	const [ contractCreateEditDialog, setContractCreateEditDialog ] = useState<BulkPlanningContractDialog | undefined>(undefined);
	const [ replanDialog, setReplanDialog ] = useState<BulkPlanningReplanDialog | undefined>(undefined);

	const selectedRowsRef = useRef(selectedRows);
	const boardRef = useRef<{ refetch: () => void }>();

	const [ getVoyage ] = useLazyQuery(GetVoyageDocument);
	const [ updateCargoActionsTimes, { loading: updateCargoActionsTimesLoading } ] = useMutation(UpdateCargoActionsTimesDocument);
	const [ changeVoyageShip ] = useMutation(CHANGE_VOYAGE_SHIP_MUTATION);
	const [ unplanAllCargosAndDeleteVoyage ] = useMutation(UnplanAllCargosAndDeleteVoyageDocument);

	const { commentsProps, openComment } = useComments({
		calls: callsForComments,
		cargos: cargosForComments as Cargo[],
		onChange: () => setTableKey(tableKey + 1),
	});

	const onDeleteVoyage = (voyage: Voyage) => {

		setLoading(true);

		unplanAllCargosAndDeleteVoyage({
			variables: {
				voyageId: voyage.id,
			},
		}).then(() => {
			refetch();
		}).finally(() => {
			setLoading(false);
		});
	};

	const createMarkers = (rows: CargoFromQuery[]) => {

		const actions = [ ...rows ].flatMap(c => c.actions)
			.map(a => ({
				from: a.start,
				to: a.end,
			})).filter((a): a is { from: number; to: number } => a.from != null && a.to != null);

		if (actions.length > 0) {

			const from = Math.min(...actions.map(a => a.from));
			const to = Math.max(...actions.map(a => a.to));

			const fromMarker = (
				<CustomMarker date={from}>
					{({ styles }) => <div style={{ ...styles, backgroundColor: "green", width: "3px" }} />}
				</CustomMarker>
			);

			const toMarker = (
				<CustomMarker date={to}>
					{({ styles }) => <div style={{ ...styles, backgroundColor: "red", width: "3px" }} />}
				</CustomMarker>
			);

			setMarkers([ fromMarker, toMarker ]);
		} else {
			setMarkers([]);
		}
	};

	const onItemChange = async(voyage: Voyage, start: number, end: number, newShip: Ship) => {

		const result = await client.query({
			query: CHARTERS_QUERY,
			variables: {
				search: {
					charterType: CharterType.Purchase,
					charterSubtypes: [ CharterSubtype.Time ],
					creditors: [ newShip.operator?.id, newShip.owner?.id ].filter(Number) as number[],
					checkForShip: newShip.id,
					timeWindowIncluded: {
						from: moment(start).unix() * 1000,
						to: moment(end).unix() * 1000,
					},
				},
			},
		});

		setReplanData({
			voyage: voyage,
			newStart: start,
			newEnd: end,
			charters: result.data.charters.content?.map(c => ({
				id: c.id,
				type: c.type,
				charterNumber: c.charterNumber,
			})) || [],
			oldShip: voyage.ship,
			newShip: {
				id: newShip.id,
				name: newShip.name,
				operator: newShip.operator,
				owner: newShip.owner,
				defaultCharterSubtype: newShip.defaultCharterSubtype,
			},
		});
	};

	const refetch = () => {
		setPlanDialog(undefined);
		enablePlanning(false);
		setReplanData(undefined);
		setTableKey(tableKey + 1);
		setSelectedRows([]);
		selectVoyage([]);

		if (boardRef.current?.refetch) {
			boardRef.current.refetch();
		}
	};

	const onLayout = (sizes: number[]) => {
		setLayoutColumnSizes(sizes);
		localStorage.setItem("bulkPlanningLayoutColumnSizes", JSON.stringify(sizes));
	};

	const replanVoyage = (actions: CargoActionsFromQuery[]) =>
		updateCargoActionsTimes({
			variables: {
				input: actions.map(toCargoActionTimesInput),
			},
		}).then(() => refetch());

	const rescheduleCallsOnVoyage = () => {
		if (!replanData) {
			return;
		}

		getVoyage({
			variables: {
				id: replanData.voyage.id,
			},
		}).then(({ data }) => {
			if (data?.voyage == null) {
				return;
			}

			let startOffset = 0;
			if (data.voyage.start) {
				startOffset = replanData.newStart - data.voyage.start;
			}

			const actions = data.voyage.calls.flatMap(c => c.cargos);

			const newActions = actions.map((a, i) => {

				let newStart = a.start;
				let newEnd = a.end;

				if (newStart && newEnd) {
					if (i == 0) {
						newStart = replanData.newStart;
						newEnd = newStart + ((a.end || 0) - (a.start || 0));
					} else if (i == actions.length - 1) {
						newStart = replanData.newEnd - ((a.end || 0) - (a.start || 0));
						newEnd = replanData.newEnd;
					} else {
						newStart = (a.start || 0) + startOffset;
						newEnd = (a.end || 0) + startOffset;
					}
				}

				return {
					...a,
					start: newStart,
					end: newEnd,
				};
			});

			const newVoyage = data.voyage;

			if (newActions.length <= 2 && replanData.charters.length > 0) {
				if (replanData.charters.some(c => c.id === newVoyage.purchaseCharters?.[0]?.id)) {
					setLoading(true);

					if (replanData.newShip.id === newVoyage.ship.id) {
						replanVoyage(newActions)
							.finally(() => setLoading(false));
					} else {
						changeVoyageShip({
							variables: {
								voyageId: replanData.voyage.id,
								shipId: replanData.newShip.id,
								charterInput: {
									id: newVoyage.purchaseCharters?.[0]?.id,
									type: CharterType.Purchase,
									subtype: CharterSubtype.Time,
								},
							},
						})
							.then(() => replanVoyage(newActions))
							.finally(() => setLoading(false));
					}

					return;

				} else if (replanData.charters.filter(c => !!c.contract).length === 1) {
					const onlyCharter = replanData.charters.filter(c => !!c.contract)[0];

					setLoading(true);

					changeVoyageShip({
						variables: {
							voyageId: replanData.voyage.id,
							shipId: replanData.newShip.id,
							charterInput: {
								id: onlyCharter.id,
								type: onlyCharter.type,
							},
						},
					})
						.then(() => replanVoyage(newActions))
						.finally(() => setLoading(false));

					return;
				}
			}

			setReplanDialog({
				open: true,
				voyage: newVoyage,
				actions: newActions,
			});
		});
	};

	useEffect(() => {
		createMarkers(selectedRows);
		selectedRowsRef.current = selectedRows;
	}, [ selectedRows ]);

	useEffect(() => {
		if (scrollToCallId) {
			setTimeout(() => {
				const element = document.getElementById(`call-task-${scrollToCallId}`);

				if (element) {
					element.scrollIntoView({ block: "end", behavior: "smooth" });
				}
			}, 100);
		}
	}, [ scrollToCallId ]);

	const componentLoading = loading || updateCargoActionsTimesLoading;

	return (
		<div id="bulkplanning">
			<PageHeader>
				<PageHeader.Title>
					{Language("BOARD")}
					<Helmet title={intl.formatMessage({ id: "BOARD" })} />
				</PageHeader.Title>
			</PageHeader>

			<div className="resize-container">
				<PanelGroup direction="horizontal" onLayout={onLayout}>
					<Panel minSize={10} defaultSize={layoutColumnSizes[0]} style={{ overflowY: "auto" }}>
						<div className="left">
							<BulkPlanningTable
								tableKey={tableKey}
								setSelectedRows={setSelectedRows}
								setDragging={(enabled) => {
									setDragging(enabled);

									if (!enabled) {
										setMarkers([]);
									}
								}}
								scrollTo={(id) => setScrollToCallId(id)}
								getCargosForComments={setCargosForComments}
								openComment={openComment} />
						</div>
					</Panel>

					<ResizeHandle />

					<Panel minSize={10} defaultSize={layoutColumnSizes[1]}>
						<div className={`right ${dragging ? "dragging" : ""} ${replanData ? "has-replan-data" : ""}`}>
							{componentLoading ? (
								<CircularProgress size={24} />
							) : (
								<React.Fragment>
									{!isReadOnly && (
										<div className="enable-planning">
											<Switch
												checked={planningEnabled}
												onChange={event => enablePlanning(event.target.checked)}
												size="small" />

											{planningEnabled ? (
												Language("DRAG_OR_DELETE_VOYAGE_TO_REPLAN")
											) : (
												<React.Fragment>
													{`${intl.formatMessage({ id: "REPLAN" })} / ${intl.formatMessage({ id: "UNPLAN" })}`}
												</React.Fragment>
											)}
										</div>
									)}

									{replanData && (
										<ReplanPreview
											data={replanData}
											onSave={() => {
												rescheduleCallsOnVoyage();
											}}
											onCancel={() => {
												setReplanData(undefined);
												selectVoyage([]);
												enablePlanning(false);
												setTimeout(() => enablePlanning(true), 1);
											}} />
									)}

									{/*@ts-expect-error*/}
									<Board
										id="bulkplanning-board"
										boardRef={boardRef}
										planboardKey={layoutColumnSizes[1]}
										renderEditableVoyages={planningEnabled}
										showAllShips={true}
										excludeBargeTypes={[ BargeType.PusherTug ]}
										enableShipLink={!planningEnabled}
										onDeleteVoyage={(voyage: Voyage) => onDeleteVoyage(voyage)}
										markers={markers}
										onItemMove={(voyage: Voyage, start: number, end: number, group: { entity: Ship }) => onItemChange(voyage, start, end, group.entity)}
										onItemResize={(voyage: Voyage, start: number, end: number, group: { entity: Ship }) => onItemChange(voyage, start, end, group.entity)}
										//@ts-expect-error
										groupRenderer={({ group }) => {
											if (group.droppable) {
												return (
													<div
														className="droppable"
														onDrop={async(event) => {
															(event.target as HTMLElement).style.background = "inherit";

															const result = await client.query({
																query: SHIP_QUERY,
																variables: {
																	id: Number(group.id),
																},
															});

															setPlanDialog({
																open: true,
																cargos: selectedRowsRef.current,
																ship: result.data.ship,
															});
														}}
														onDragOver={(event) => event.preventDefault()}
														onDragEnter={(event) => {
															event.preventDefault();
															(event.target as HTMLElement).style.background = "lightblue";
														}}
														onDragLeave={(event) => {
															event.preventDefault();
															(event.target as HTMLElement).style.background = "inherit";
														}}>
														{group.title}
													</div>
												);
											} else {
												return (
													<div>
														{group.title}
													</div>
												);
											}
										}}
										selected={selectedVoyage}
										//@ts-expect-error
										onItemSelect={(voyage, start, end, group, event) => {
											const isDeleteButton = event.target.closest(".delete-button");

											if (!replanData && !isDeleteButton && planningEnabled) {
												selectVoyage([ voyage?.id ]);
												onItemChange(voyage, start, end, group.entity);
											}
										}}
										onItemDeselect={() => {
											if (!replanData && planningEnabled) {
												selectVoyage([]);
											}
										}}
										getCallsForComments={setCallsForComments} />
								</React.Fragment>
							)}
						</div>
					</Panel>
				</PanelGroup>
			</div>

			{planDialog?.open && (
				<CargoPlanningDialog
					open={planDialog.open}
					onClose={() => setPlanDialog(undefined)}
					cargos={planDialog.cargos}
					ship={planDialog.ship}
					onSave={(data: CargoLinkedCharter, createSalesContract) => setContractCreateEditDialog({ open: true, charter: data, createSalesContract })}
					refetch={refetch} />
			)}

			{contractCreateEditDialog?.open && (
				<ContractCreateEditDialog
					open={contractCreateEditDialog.open}
					onClose={() => setContractCreateEditDialog(undefined)}
					charter={contractCreateEditDialog.charter}
					createSalesContract={contractCreateEditDialog.createSalesContract}
					refetch={refetch} />
			)}

			{replanDialog?.open && (
				<UnplannedCargoReplanDialog
					voyage={replanDialog.voyage}
					charters={replanData?.charters}
					actions={replanDialog.actions}
					newShip={replanData?.newShip}
					onClose={() => setReplanDialog(undefined)}
					callback={refetch} />
			)}

			<CommentsWidget {...commentsProps} />
		</div>
	);
};

const ReplanPreview = ({ data, onSave, onCancel }: {
	data: BulkPlanningReplan;
	onSave: () => void;
	onCancel: () => void;
}) => {

	const { voyage, oldShip, newShip, newStart, newEnd } = data;

	return (
		<div className="replan-preview">
			<Card
				title={`${voyage.voyageNumber} - ${voyage.purchaseCharters?.[0]?.charterNumber} - ${voyage.from?.description} ${intl.formatMessage({ id: "TO_LOWERCASE" })} ${voyage.to?.description}`}
				actions={[ {
					icon: <HighlightOffIcon />,
					onClick: () => onCancel(),
				}, {
					icon: <SaveIcon />,
					onClick: () => onSave(),
				} ]}>
				<p>{intl.formatMessage({ id: "SHIP" })}: <span className="line-through">{oldShip.name}</span> {`| ${newShip.name}`}</p>
				<p>
					{intl.formatMessage({ id: "VOYAGE_START_OR_END" })}:

					{" "}

					<span className="line-through">
						<FormattedDateWindow from={voyage.start} to={voyage.end} withoutYear />
					</span>

					{" | "}

					<FormattedDateWindow from={newStart} to={newEnd} withoutYear />
				</p>
			</Card>
		</div>
	);
};

const CHARTERS_QUERY = gql(`#graphql
    query charters($search: CharterSearch!, $pagination: Pagination) {
        charters(search: $search, pagination: $pagination) {
            totalElements
            content {
                id
				charterNumber
				type
				creditor {
					id
					name
                }
				contract {
					id
				}
				... on PurchaseCharter {
					validFrom
					validUntil
				}
            }
        }
    }
`);

const CHANGE_VOYAGE_SHIP_MUTATION = gql(`#graphql
    mutation changeVoyageShipAndCharter($voyageId: Long!, $shipId: Long!, $charterInput: CharterInput!) {
        changeVoyageShipAndCharter(voyageId: $voyageId, shipId: $shipId, charterInput: $charterInput)
    }
`);

const SHIP_QUERY = gql(`#graphql
    query getShipFromGroup($id: Long!) {
        ship(id: $id) {
           id
			name
			eni
			mmsi
			available
			fuelType
			defaultCharterSubtype
			operator {
				id
				name
				applicationRelation
			}
			owner {
				id
				name
				applicationRelation
			}
			agents {
				location {
					id
					description
				}
				agent {
					id
					name
				}
			}
        }
    }
`);



export default BulkPlanning;
